// api-client — thin fetch wrapper. Always sends correlation_id when caller
// passes one; otherwise lets the gateway mint one.

import { dispatchSessionExpired } from "./session-expiry.js";

// `import.meta.env` is a Vite injection — undefined in pure-Node test runs.
// Read defensively so node:test (tsx) can `await import("./logout.js")` →
// `./api-client.js` without crashing on a missing env object.

/** Frontend host → matching API host. NEXUS rebrand Phase 2 coexistence: one
 *  Pages build serves BOTH nexus.sentry.red and nexus.sentry.red, so the
 *  API base MUST follow the host the SPA is currently served on — otherwise a
 *  session cookie scoped to .nexus.sentry.red is never sent to
 *  api.nexus.sentry.red and every dashboard call 401s. Unknown host →
 *  null (caller falls back to VITE_API_BASE for previews / dev). */
export function apiBaseForHost(host: string): string | null {
  if (host === "nexus.sentry.red") return "https://api.nexus.sentry.red";
  if (host === "nexus.sentry.red") return "https://api.nexus.sentry.red";
  return null;
}

function envApiBase(): string {
  // NEXUS rebrand Phase 2 (group 8) hard cutover: the production default is
  // nexus. In the browser, resolveApiBase() host-matches FIRST (so a
  // location-host SPA still reaches api.location during coexistence); this
  // fallback is only hit for previews / dev / node tests. VITE_API_BASE remains
  // the explicit dev override.
  return (
    ((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE) ??
    "https://api.nexus.sentry.red"
  );
}

/** API base for the current document: host-matched in the browser, else
 *  VITE_API_BASE (preview / dev / node tests). */
export function resolveApiBase(): string {
  if (typeof window !== "undefined") {
    const hostBase = apiBaseForHost(window.location.hostname);
    if (hostBase) return hostBase;
  }
  return envApiBase();
}

export const API_BASE: string = resolveApiBase();

export interface ApiClientOptions {
  correlationId?: string;
  jwt?: string;
  /** Opaque scan_session token issued by `POST /v1/patrols/scan/match`.
   *  Sent as `x-scan-session` to authenticate as a (tenant, officer)
   *  pair derived from a successful NTAG tap + identity verify. Used by
   *  the public scan wizard (`/scan/wizard`) which never has a JWT.
   *  Mutually exclusive with `jwt` — the gateway prefers the JWT when
   *  both are present, so callers must pass exactly one. */
  scanSession?: string;
}

// Paths whose 401 must NOT trigger the global session-expiry redirect.
//
// /v1/auth/* — probes whether a cookie is valid (e.g. /auth/me on the public
//   landing page) or accepts credentials we haven't sent yet
//   (login / register / refresh / verify-email / forgot-password).
//   Bouncing the user to /login from a 401 on these paths would loop /
//   break the login form itself.
//
// /v1/ai/* — the AI chat assistant is an auxiliary, opt-in feature
//   (chat bubble + ChatGPT-style panel). A 401 here can arise from
//   ai-api worker config drift, registry mismatch, or any per-feature
//   auth quirk that has nothing to do with the user's actual session
//   being dead. If the nexus_session cookie is genuinely expired, every
//   non-AI endpoint will also 401 and one of those will trigger expiry;
//   silently failing the AI call instead of auto-logging-out is the
//   right tradeoff. (chatApi's asError() turns the 401 into an
//   AiChatApiError and ChatBubble surfaces it as "AI 暫時無法使用".)
const NON_FATAL_401_PATHS = ["/v1/auth/", "/v1/ai/"];

function isNonFatal401(path: string): boolean {
  return NON_FATAL_401_PATHS.some((p) => path.startsWith(p));
}

export async function apiFetch(path: string, init: RequestInit = {}, opts: ApiClientOptions = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (opts.jwt) headers.set("authorization", `Bearer ${opts.jwt}`);
  if (opts.scanSession) headers.set("x-scan-session", opts.scanSession);
  if (opts.correlationId) headers.set("x-correlation-id", opts.correlationId);
  // AI Plan & Apply (docs/specs/ai-plan-and-apply.md §9.2) — when a plan
  // dispatcher is mid-batch, every outbound request inherits the
  // plan_correlation_id so the resulting audit rows group together
  // back in dashboard /audit. The carrier is a transient global set in
  // PlanModal.dispatchOperation's try/finally; cleared between ops.
  // No-op when the global isn't set (99.9% of requests).
  const planCid = (globalThis as { __pendingPlanCorrelationId?: string }).__pendingPlanCorrelationId;
  if (planCid) headers.set("x-plan-correlation-id", planCid);
  // BASE is on a different origin (api.nexus.sentry.red) from where the
  // SPA loads (nexus.sentry.red). fetch defaults to "same-origin", which
  // omits the nexus_session cookie — and because both `auth-api` and every
  // tenant-scoped endpoint authenticate via that cookie, every uncredentialed
  // request 401s. Forcing "include" at the wrapper means every caller is
  // safe by construction. Callers that explicitly want anonymous (e.g. a
  // public endpoint where we deliberately skip cookies) can still override
  // by passing `credentials: "omit"` in init.
  const credentials: RequestCredentials = init.credentials ?? "include";
  // Host-aware base (resolved per call): on nexus.sentry.red this targets
  // api.nexus.sentry.red so the .nexus session cookie is actually sent.
  const res = await fetch(`${resolveApiBase()}${path}`, { ...init, credentials, headers });
  // Global session-expiry hook: any non-auth 401 means the cookie /
  // JWT we sent was rejected by the gateway. SessionExpiryWatcher
  // (mounted at SPA root) decides what to do — usually a redirect to
  // /login with `?reason=session_expired` for dashboard routes. We
  // still return the response so the caller's own error path runs.
  if (res.status === 401 && !isNonFatal401(path)) {
    dispatchSessionExpired();
  }
  return res;
}
